import React, { useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { ref, remove, set } from 'firebase/database';
import { auth, database } from '../api/firebase';
import Split from 'react-split';
import VideoPlayer from '../components/VideoPlayer';
import SideBar from '../components/SideBar';
import useRoomcode from '../hooks/useRoomcode';
import useNickname, { NICKNAME_PLACEHOLDER } from '../hooks/useNickname';
import { SM_MIN_WIDTH } from '../constants/media-query';
import { useMediaQuery } from 'react-responsive';
import useLogger, { Logger } from '../hooks/useLogger';

interface RoomProps { };

const Room: React.FC<RoomProps> = () => {
  const uid = auth.currentUser!.uid; // guaranteed user since this is inside AuthFilter
  const roomcode = useRoomcode();    // guaranteed roomcode since this is inside RoomFilter
  const nickname = useNickname(uid, roomcode);
  const logger: Logger = useLogger();
  const isSm: boolean = useMediaQuery({ minWidth: SM_MIN_WIDTH });

  useEffect(() => {
    nickname === NICKNAME_PLACEHOLDER
      ? logger.info(`pending nickname for uid '${uid}'`)
      : logger.info(`user '${nickname}' joined roomcode '${roomcode}'`);
  }, [uid, logger, nickname, roomcode]);

  useEffect(
    () => {
      // on tab close or back-button press, leave the room
      const leaveRoom = async () => {
        const currentUserRef = ref(database, `bubbles/${roomcode}/users/${auth.currentUser!.uid}`);
        await remove(currentUserRef);
      };
      const leaveOrRejoinRoom = async () => {
        if (isMobile) {
          const currentUserRef = ref(database, `bubbles/${roomcode}/users/${auth.currentUser!.uid}`);
          if (document.visibilityState === 'hidden') {
            await remove(currentUserRef);
          } else {
            await set(currentUserRef, { nickname });
          }
        }
      }
      const unsubscribe = () => {
        window.removeEventListener('beforeunload', leaveRoom);
        window.removeEventListener('popstate', leaveRoom);
        window.removeEventListener('visibilitychange', leaveOrRejoinRoom);
      };
      window.addEventListener('beforeunload', leaveRoom);
      window.addEventListener('popstate', leaveRoom);
      window.addEventListener('visibilitychange', leaveOrRejoinRoom);
      return unsubscribe;
    },
    [roomcode, nickname]
  );

  const wrapperClass: string = 'h-screen flex flex-col-reverse justify-end sm:flex-row sm:justify-center';

  return (
    isSm ?
      <Split
        sizes={[20, 80]}
        minSize={0}
        direction={'horizontal'}
        className={wrapperClass}
      >
        <SideBar />
        <VideoPlayer />
      </Split>
      :
      <div className={wrapperClass}>
        <SideBar />
        <VideoPlayer />
      </div>
  );
}

export default Room;
